/**
 * ── O NÚCLEO DO MOTOR DE TRADUÇÃO: A ESCADA, UMA SÓ VEZ ────────────────────
 *
 * `chave → texto` com interpolação, e nada mais. Ele existe separado porque há
 * **dois universos** a pedir a mesma regra com uma base diferente:
 *
 * ```
 * servidor (motor.ts) ->  base = `pt`, as 533 chaves         (Node, sem rede)
 * tela     (tela.ts)  ->  base = `CASCA`, as 100 da moldura  (+ folhas que descem)
 * ```
 *
 * Escrever a escada duas vezes seria a coisa que esta casa recusa por escrito:
 * duas soluções para a mesma pergunta divergem, e a que divergir em silêncio
 * é a da tela — onde o sintoma é texto cru e ninguém abre defeito por uma
 * frase feia. Por isso a regra vive aqui, e os dois motores são a MESMA
 * função com outra base.
 *
 * ## A escada, e por que o último degrau é a própria chave
 *
 * `dicionário da língua → base → folhas registadas → a chave`.
 *
 * O último degrau só se alcança com uma chave que não existe em lado nenhum —
 * e mostrar `sala.concluir` ao aluno é feio de propósito: é o que faz alguém
 * abrir um defeito. As duas alternativas são piores: esconder o texto deixa um
 * botão sem rótulo, e devolver vazio faz a tela parecer certa.
 *
 * ## As FOLHAS: por que registar, em vez de importar
 *
 * A tela do aluno não pode levar as 433 chaves do conteúdo no pedaço de
 * entrada (`casca.ts` tem a medição). Elas descem com a tela, e a tela
 * entrega-as ao motor por `registarFolha`. O motor não sabe de pedaços nem de
 * rede: sabe que a base pode crescer.
 */

/** `{nome}` vira o valor; o que não tiver valor **fica como está**. */
export type Valores = Record<string, string | number>;

/** Uma folha de frases — a base, um dicionário de língua, ou um pedaço que desceu. */
export type Folha = Readonly<Record<string, string>>;

/**
 * `{nome}` vira o valor; o que não tiver valor **fica como está**.
 *
 * Deixar o `{nome}` cru à vista é deliberado: é feio, e é para ser. Apagá-lo
 * produziria uma frase que parece certa e perdeu um dado — «Olá, » em vez de
 * «Olá, {nome}» —, e ninguém abre um defeito por uma frase que parece certa.
 */
export function interpolar(texto: string, valores: Valores): string {
  return texto.replace(/\{(\w+)\}/g, (cru, nome: string) =>
    nome in valores ? String(valores[nome]) : cru,
  );
}

export interface Motor {
  /** A frase, na língua já carregada. Nunca lança e nunca devolve vazio. */
  traduzir(dicionario: Folha | null | undefined, chave: string, valores?: Valores): string;
  /** Um tradutor preso a um dicionário — o que as telas e as rotas usam. */
  tradutorDe(dicionario: Folha | null | undefined): (chave: string, valores?: Valores) => string;
  /**
   * Acrescenta uma folha ao fallback da base.
   *
   * Idempotente de propósito: toda tela do aluno registra a mesma folha ao
   * descer, e registar N vezes não pode custar N buscas por chave.
   */
  registarFolha(folha: Folha): void;
  /** As chaves que o motor conhece agora — a base mais o que já foi registado. */
  chavesConhecidas(): string[];
}

export function criarMotor(base: Folha): Motor {
  const folhas: Folha[] = [];

  function daBase(chave: string): string | undefined {
    const naBase = base[chave];
    if (naBase !== undefined) return naBase;
    for (const folha of folhas) {
      const nela = folha[chave];
      if (nela !== undefined) return nela;
    }
    return undefined;
  }

  function traduzir(
    dicionario: Folha | null | undefined,
    chave: string,
    valores?: Valores,
  ): string {
    const texto = dicionario?.[chave] ?? daBase(chave) ?? chave;
    return valores ? interpolar(texto, valores) : texto;
  }

  return {
    traduzir,
    /*
     * `tradutorDe` devolve uma closure, e é por isso que quem a usa em React a
     * guarda num `useMemo`: recriá-la a cada render faria todo componente que a
     * receba por prop repintar sem motivo.
     */
    tradutorDe: (dicionario) => (chave, valores) => traduzir(dicionario, chave, valores),
    registarFolha: (folha) => {
      if (!folhas.includes(folha)) folhas.push(folha);
    },
    chavesConhecidas: () => [
      ...new Set([...Object.keys(base), ...folhas.flatMap((f) => Object.keys(f))]),
    ],
  };
}
